/*
 * Copyright (c) 2017 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.docking.js.splitter

import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.docking.splitter.AbstractThreeComponentsSplitter
import com.macrofocus.docking.splitter.ThreeComponentsLayout
import js.coroutines.awaitCancellation
import js.objects.unsafeJso
import org.mkui.canvas.ResizeObserver
import org.mkui.component.CPComponent
import org.mkui.react.useProperty
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.events.PointerEvent
import react.dom.html.ReactHTML.div
import react.useEffectOnce
import react.useRef
import react.useState
import web.cssom.*
import web.html.HTMLDivElement

/**
 * React implementation of the three components splitter. Sizes are expressed in CSS pixels and the layout is computed
 * by [ThreeComponentsLayout], which mirrors the reference Swing implementation.
 */
class CPThreeComponentsSplitter constructor(vertical: Boolean = false) : AbstractThreeComponentsSplitter<CPComponent?>(), CPComponent {
    private val firstComponentProperty: MutableProperty<CPComponent?> = SimpleProperty(null)
    private val innerComponentProperty: MutableProperty<CPComponent?> = SimpleProperty(null)
    private val lastComponentProperty: MutableProperty<CPComponent?> = SimpleProperty(null)

    private val firstSizeProperty: MutableProperty<Double> = SimpleProperty(350.0)
    private val lastSizeProperty: MutableProperty<Double> = SimpleProperty(10.0)

    private val verticalProperty: MutableProperty<Boolean> = SimpleProperty(vertical)
    private val isRelativeResizeProperty: MutableProperty<Boolean> = SimpleProperty(false)
    private val showDividerControls: MutableProperty<Boolean> = SimpleProperty(true)

    /** Width of the dividers, in CSS pixels. */
    var dividerWidth: Int = ThreeComponentsLayout.DEFAULT_DIVIDER_WIDTH

    /** Divider being dragged: pointer position and divider position when pressed. */
    private class Drag(val first: Boolean, val pointer: Double, val dividerStart: Int, val total: Int)
    private var drag: Drag? = null

    val nc: FC<*> = FC<Props> {
        val (vertical, _) = useProperty(verticalProperty)
        val (myFirstComponent, _) = useProperty(firstComponentProperty)
        val (myInnerComponent, _) = useProperty(innerComponentProperty)
        val (myLastComponent, _) = useProperty(lastComponentProperty)
        val (firstSize, _) = useProperty(firstSizeProperty)
        val (lastSize, _) = useProperty(lastSizeProperty)

        val containerRef = useRef<HTMLDivElement>(null)
        val (measuredWidth, setWidth) = useState(0)
        val (measuredHeight, setHeight) = useState(0)

        useEffectOnce {
            val element = containerRef.current
            if (element != null) {
                val update = {
                    setWidth(element.clientWidth)
                    setHeight(element.clientHeight)
                }
                update()
                val observer = ResizeObserver { _, _ -> update() }
                observer.observe(element)
                awaitCancellation {
                    observer.disconnect()
                }
            }
        }

        val total = if (vertical) measuredHeight else measuredWidth
        val bounds = ThreeComponentsLayout.compute(
            total = total,
            firstVisible = myFirstComponent != null,
            innerVisible = myInnerComponent != null,
            lastVisible = myLastComponent != null,
            firstSize = firstSize,
            lastSize = lastSize,
            dividerWidth = dividerWidth,
        )

        div {
            className = ClassName("ThreeComponentsSplitter")
            ref = containerRef
            style = unsafeJso {
                position = Position.relative
                width = 100.pct
                height = 100.pct
                minWidth = 0.px
                minHeight = 0.px
                overflow = Overflow.hidden
            }

            slot("first", vertical, bounds.firstStart, bounds.firstSize, myFirstComponent)
            if (bounds.firstDividerVisible) {
                divider("firstDivider", vertical, bounds.firstDividerStart, bounds.firstDividerSize, true, total)
            }
            slot("inner", vertical, bounds.innerStart, bounds.innerSize, myInnerComponent)
            if (bounds.lastDividerVisible) {
                divider("lastDivider", vertical, bounds.lastDividerStart, bounds.lastDividerSize, false, total)
            }
            slot("last", vertical, bounds.lastStart, bounds.lastSize, myLastComponent)
        }
    }

    private fun ChildrenBuilder.slot(name: String, vertical: Boolean, start: Int, size: Int, component: CPComponent?) {
        if (component == null) return
        div {
            key = name.unsafeCast<react.Key>()
            className = ClassName("ThreeComponentsSplitter-$name")
            style = unsafeJso {
                position = Position.absolute
                overflow = Overflow.hidden
                boxSizing = BoxSizing.borderBox
                pointerEvents = Auto.auto
                if (vertical) {
                    left = 0.px
                    right = 0.px
                    top = start.px
                    height = size.px
                } else {
                    top = 0.px
                    bottom = 0.px
                    left = start.px
                    width = size.px
                }
            }
            component.nativeComponent.invoke()
        }
    }

    private fun pointerPosition(event: PointerEvent<HTMLDivElement>, vertical: Boolean): Double =
        if (vertical) event.clientY else event.clientX

    private fun ChildrenBuilder.divider(name: String, vertical: Boolean, start: Int, size: Int, first: Boolean, total: Int) {
        div {
            key = name.unsafeCast<react.Key>()
            className = ClassName("ThreeComponentsSplitter-$name")
            style = unsafeJso {
                position = Position.absolute
                display = Display.flex
                alignItems = AlignItems.center
                justifyContent = JustifyContent.center
                pointerEvents = Auto.auto
                touchAction = None.none
                cursor = if (vertical) Cursor.rowResize else Cursor.colResize
                if (vertical) {
                    left = 0.px
                    right = 0.px
                    top = start.px
                    height = size.px
                } else {
                    top = 0.px
                    bottom = 0.px
                    left = start.px
                    width = size.px
                }
            }

            onPointerDown = { event ->
                event.preventDefault()
                // Keep receiving the moves when the pointer leaves the divider (may fail for synthetic pointers)
                try {
                    event.currentTarget.setPointerCapture(event.pointerId)
                } catch (_: Throwable) {
                }
                drag = Drag(first, pointerPosition(event, vertical), start, total)
            }
            onPointerMove = { event ->
                val d = drag
                if (d != null) {
                    // Like Swing, the divider follows the pointer
                    val position = d.dividerStart + pointerPosition(event, vertical) - d.pointer
                    if (d.first) {
                        firstSize = ThreeComponentsLayout.firstSizeForDividerAt(position)
                    } else {
                        lastSize = ThreeComponentsLayout.lastSizeForDividerAt(position, d.total.toDouble(), dividerWidth)
                    }
                }
            }
            onPointerUp = { event ->
                drag = null
                try {
                    event.currentTarget.releasePointerCapture(event.pointerId)
                } catch (_: Throwable) {
                }
            }

            // Grip, similar to the dotted glue of the Swing divider
            div {
                style = unsafeJso {
                    if (vertical) {
                        width = 20.px
                        height = 0.px
                        borderTop = Border(2.px, LineStyle.dotted, rgb(138, 138, 138))
                    } else {
                        width = 0.px
                        height = 20.px
                        borderLeft = Border(2.px, LineStyle.dotted, rgb(138, 138, 138))
                    }
                }
            }
        }
    }

    override val nativeComponent: FC<*> = nc

    override var vertical by verticalProperty
    override var isRelativeResize: Boolean by isRelativeResizeProperty
    override var firstComponent: CPComponent? by firstComponentProperty
    override var innerComponent: CPComponent? by innerComponentProperty
    override var lastComponent: CPComponent? by lastComponentProperty

    override fun setComponents(first: CPComponent?, inner: CPComponent?, last: CPComponent?) {
        firstComponent = first
        innerComponent = inner
        lastComponent = last
    }

    override var firstSize: Double by firstSizeProperty

    override var lastSize: Double by lastSizeProperty

    override val firstSizeIfVisible: Double
        get() = if (firstComponent != null) firstSize else 0.0

    override val lastSizeIfVisible: Double
        get() = if (lastComponent != null) lastSize else 0.0

    override fun setWidgetHidden(widget: CPComponent?, hidden: Boolean) {
    }

    override fun setShowDividerControls(show: Boolean) {
        showDividerControls.value = show
    }
}
